import React from "react"

import Box from "@material-ui/core/Box"
import Button from "@material-ui/core/Button"
import CircularProgress from "@material-ui/core/CircularProgress"
import List from "@material-ui/core/List"
import Typography from "@material-ui/core/Typography"

import FileListItem from "./FileListItem"
import { StorageFileViewModel } from "../api/storage-file"
import { useGetStorageFilesQuery } from "../api"
import { useFileMutations } from "../hooks/use-file-mutation"
import { useFileUpload } from "../hooks/use-file-upload"
import { useFileDownload } from "../hooks/use-file-download"
import { useTranslation } from "react-i18next"

import { COLOR_DIVIDER } from "../../constants"
import { useAppContext } from "../../app-context/use-app-context"

const FileList = ({ entityId, entityType, canUploadFiles, canDeleteFiles }: FileListProps) => {
  const { filesLabel, filesEmptyMessage, uploadButtonLabel } = useTranslations()

  const {
    data: files = [],
    isLoading,
    refetch,
  } = useGetStorageFilesQuery({
    entityId,
    entityType,
  })

  const { uploadFiles } = useFileUpload()
  const { deleteFile } = useFileMutations()
  const { downloadFile, openFileInBrowser } = useFileDownload()

  const [uploading, setUploading] = React.useState(false)
  const [downloadingFileId, setDownloadingFileId] = React.useState<string | null>(null)
  const [deletingFileId, setDeletingFileId] = React.useState<string | null>(null)
  const { appContext } = useAppContext()
  const orgId = appContext?.mainContext?.type === "org" ? appContext.mainContext.id : undefined

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target
    const selectedFiles = Array.from(input.files || [])

    if (!selectedFiles.length) return

    const payload = orgId
      ? { files: selectedFiles, entityId, entityType, orgId }
      : { files: selectedFiles, entityId, entityType }

    setUploading(true)

    try {
      await uploadFiles(payload)
      await refetch()
    } finally {
      setUploading(false)
      input.value = ""
    }
  }

  const handleDelete = async (fileId: string) => {
    setDeletingFileId(fileId)

    try {
      await deleteFile(fileId)
      refetch()
    } finally {
      setDeletingFileId(null)
    }
  }
  const handleDownload = async (fileId: string) => {
    setDownloadingFileId(fileId)

    try {
      await downloadFile(fileId)
    } finally {
      setDownloadingFileId(null)
    }
  }
  if (isLoading) return <CircularProgress />

  const fileCount = files.length

  const emptyMessage = (
    <Typography style={{ textAlign: "center", marginBottom: 12 }} component="p">
      {filesEmptyMessage}
    </Typography>
  )

  const uploadButton = (
    <Button
      disabled={uploading}
      style={{ minWidth: "120px", paddingTop: 4, paddingBottom: 4 }}
      component="label"
      size="small"
      variant="outlined"
      color="primary"
    >
      {uploading ? <CircularProgress size={20} /> : uploadButtonLabel}
      <input hidden multiple type="file" onChange={handleUpload} />
    </Button>
  )

  return (
    <>
      <Typography variant="subtitle1" gutterBottom>
        <strong>{filesLabel}</strong>
      </Typography>

      {files.length > 0 && (
        <List
          dense
          style={{
            maxWidth: "460px",
            border: fileCount ? `1px solid ${COLOR_DIVIDER}` : "none",
            borderRadius: 4,
          }}
        >
          {files.map((file: StorageFileViewModel, index: number) => (
            <FileListItem
              key={file.id}
              file={file}
              canDelete={canDeleteFiles}
              onDelete={() => handleDelete(file.id)}
              onDownload={() => handleDownload(file.id)}
              onOpenInBrowser={() => openFileInBrowser(file.id)}
              loadingDelete={deletingFileId === file.id}
              loadingDownload={downloadingFileId === file.id}
              divider={index !== files.length - 1}
            />
          ))}
        </List>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: fileCount ? undefined : "center",
          py: 2,
          borderRadius: 4,
          border: fileCount ? "none" : `1px dashed ${COLOR_DIVIDER}`,
        }}
      >
        {fileCount ? null : emptyMessage}
        {canUploadFiles ? <Box>{uploadButton}</Box> : null}
      </Box>
    </>
  )
}

type FileListProps = {
  entityId: string
  entityType: "project" | "task"
  canUploadFiles: boolean
  canDeleteFiles: boolean
}

const defaultTranslations = {
  filesLabel: "Files",
  filesEmptyMessage: "There are no files here yet.",
  uploadButtonLabel: "Upload files",
}
const useTranslations = () => {
  const { t } = useTranslation("storage-file")

  return {
    filesLabel: t("files", defaultTranslations.filesLabel),
    filesEmptyMessage: t("filesEmptyMessage", defaultTranslations.filesEmptyMessage),
    uploadButtonLabel: t("uploadButtonLabel", defaultTranslations.uploadButtonLabel),
  }
}
export default FileList
